/**
 * The Sketch's layouts by window width (phase 6): `desktop` (the side panel beside the map, the rail and the Assist
 * bar floating on it), `tablet` (≤ 900 px: the side panel becomes a bottom sheet, the Assist bar folds into a button)
 * and `phone` (≤ 640 px: also the tool rail becomes a collapsible bar at the bottom of the map). sketch.css has the
 * same breakpoints. Pure, plus the hook that follows the window.
 */
import { useEffect, useState } from "react";

export type SketchLayout = "desktop" | "tablet" | "phone";
export const TABLET_MAX_PX = 900;
export const PHONE_MAX_PX = 640;

export function layoutFor(width: number): SketchLayout {
  return width <= PHONE_MAX_PX ? "phone" : width <= TABLET_MAX_PX ? "tablet" : "desktop";
}

/** The bottom sheet's state on a narrow screen: `peek` shows the system size and a handle, `open` the whole panel. */
export type SheetState = "peek" | "open";

/** A vertical swipe on the sheet's handle (pixels, down positive): up opens, down peeks, a tap toggles. */
export function sheetAfterSwipe(state: SheetState, dy: number): SheetState {
  if (Math.abs(dy) < 12) return state === "open" ? "peek" : "open";
  return dy < 0 ? "open" : "peek";
}

export function useSketchLayout(): SketchLayout {
  const [layout, setLayout] = useState<SketchLayout>(() => (typeof window === "undefined" ? "desktop" : layoutFor(window.innerWidth)));
  useEffect(() => {
    const on = () => setLayout(layoutFor(window.innerWidth));
    window.addEventListener("resize", on);
    on();
    return () => window.removeEventListener("resize", on);
  }, []);
  return layout;
}
